import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// INTERFACES
import { finalize, tap } from 'rxjs/operators';

import { ChannelApiService } from '../shared/services/api/channel/channel-api.service';

import { IChannel } from './../shared/interfaces/mtchannel.interface';
import {
  IPagination,
  IPaginationResponse,
} from './../shared/interfaces/paginator.interface';

// SERVICES

import { ChannelStateService } from './channel-state.service';
import { IChannelState } from './interfaces';

@Injectable({
  providedIn: 'root',
})
export class ChannelFacadeService {
  constructor(
    private channelStateService :ChannelStateService,
    private channelApiService :ChannelApiService,
  ) {}

  // ACTIONS
  getChannels(params :{
    [param :string] :string | string[];
  }) :Observable<IPaginationResponse> {
    this.channelStateService.update({ loading: true });

    return this.channelApiService.getV2(params).pipe(
      finalize(() => this.channelStateService.update({ loading: false })),
      tap((response :IPaginationResponse) => {
        const { data, meta } = response;
        this.channelStateService.update({ channels: data, meta });
      }),
    );
  }

  getById(data :{
    id :number;
    params :{ [param :string] :string | string[] };
  }) :Observable<IChannel> {
    const { id, params } = data;
    this.channelStateService.update({ loading: true });

    return this.channelApiService.getById(id, params).pipe(
      finalize(() => this.channelStateService.update({ loading: false })),
      tap((response :IChannel) => {
        this.channelStateService.update({ currentChannel: response });
      }),
    );
  }

  create(channel :Partial<IChannel>) :Observable<any> {
    this.channelStateService.update({ submitting: true });

    return this.channelApiService
      .createV2(channel)
      .pipe(finalize(() => this.channelStateService.update({ submitting: false })));
  }

  update(params :{ id :number; data :Partial<IChannel> }) :Observable<any> {
    const { id, data } = params;

    this.channelStateService.update({ submitting: true });

    return this.channelApiService
      .updateV2({ id, data })
      .pipe(finalize(() => this.channelStateService.update({ submitting: false })));
  }

  updateStatus(data :{
    id :number;
    params :{ [param :string] :string | string[] };
  }) :Observable<IChannel> {
    const { id, params } = data;

    return this.channelApiService.updateStatus({ id, params });
  }

  delete(id :number) :Observable<any> {
    this.channelStateService.update({ loading: true });

    return this.channelApiService.delete(id).pipe(
      finalize(() => this.channelStateService.update({ loading: false })),
      tap(() => {
        this.getChannels(this.currentQueryParams).subscribe();
      }),
    );
  }

  setCurrentQueryParams(params :{ [param :string] :string | string[] }) :void {
    this.channelStateService.update({ currentQueryParams: params });
    this.getChannels(params).subscribe();
  }

  updateState(data :Partial<IChannelState>) :void {
    this.channelStateService.update(data);
  }

  renewQuickbooksToken(id :number) :Observable<any> {
    return this.channelApiService.renewQuickbooksToken(id);
  }

  renewHubSpotTokens(id :number) :Observable<any> {
    return this.channelApiService.renewHubSpotTokens(id);
  }

  authorizeEcartApi(id :number) :Observable<any> {
    return this.channelApiService.renewHubSpotTokens(id);
  }

  // STATES GETTERS
  get channelState() :IChannelState {
    return this.channelStateService.getSnapshot();
  }

  // DIRECT VALUE ACCESSORS
  get currentQueryParams() :{ [key :string] :string | string[] } {
    return this.channelStateService.getSnapshot().currentQueryParams;
  }

  get pagination() :IPagination {
    return this.channelStateService.getSnapshot().meta;
  }

  // OBSERVABLES ACCESSORS
  get channels$() :Observable<IChannel[]> {
    return this.channelStateService.channels$;
  }

  get currentChannel$() :Observable<IChannel | null> {
    return this.channelStateService.currentChannel$;
  }

  get submitting$() :Observable<boolean> {
    return this.channelStateService.submitting$;
  }

  get loading$() :Observable<boolean> {
    return this.channelStateService.loading$;
  }
}
